<script lang="ts">
import { defineComponent, PropType, inject, ref, computed } from 'vue'
import InputProfileCareer from '@/components/lead/InputProfileCareer.vue'
import InputProfileName from '@/components/lead/InputProfileName.vue'
import StepWizard from '@/components/step/StepWizard.vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import FormDefault from '@/components/form/FormDefault.vue'
import validationOperator from '@/models/validationOperator'
import { formPartial } from '@/models/formPartial'

export default defineComponent({
  name: 'ModalProfileForm',

  components: {
    InputProfileCareer,
    InputProfileName,
    StepWizard,
    ValidationObserver,
    ButtonSubmit,
    FormDefault,
  },

  props: {
    /**
     * フォーム情報
     */
    form: {
      type: Object as PropType<Form>,
      required: true,
    },

    userLeadInfo: {
      type: Object as PropType<UserLeadInfo>,
      required: true,
    },

    /**
     * モーダル表示制御フラグ
     */
    isShow: {
      type: Boolean,
      required: true,
    },
  },

  emits: [
    'update:lastName',
    'update:firstName',
    'update:lastNameKana',
    'update:firstNameKana',
    'update:companyName',
    'select:companyLeadInfo',
    'reset:corporateZipcode',
    'update:corporateAddress1',
    'update:industry',
    'update:scale',
    'update:profession',
    'update:positionLevel',
    'update:departmentAndPosition',
    'error',
  ],

  setup(props) {
    // constants
    const EXTERNAL_URL = inject<Record<string, string>>('EXTERNAL_URL') || {}

    const { validationObserver, validate } = validationOperator()
    const { createErrorMessage } = formPartial()
    const validationObserverProfile = ref()
    const validationObserverCareer = ref()

    const isShowModal = ref(props.isShow)
    const stepWizardEl = ref()
    const formEl = ref<InstanceType<typeof FormDefault> | null>(null)

    const namespaceByStep = computed(() => {
      return {
        profile: `${props.form.name}-profile`,
        career: `${props.form.name}-career`,
      }
    })

    const submit = () => {
      if (formEl.value) {
        formEl.value.submit()
      }
    }

    return {
      // constants
      EXTERNAL_URL,

      // data
      validationObserver,
      validationObserverProfile,
      validationObserverCareer,
      isShowModal,
      stepWizardEl,
      formEl,

      // computed
      namespaceByStep,

      // methods
      validate,
      submit,
      createErrorMessage,
    }
  },
})
</script>

<template>
  <ModalDefault
    :is-show="isShow"
    :is-black-out="true"
    :is-blur="true"
    :enable-backdrop="false"
    :enable-close="false"
    header-message="この機能のご利用にはプロフィールを入力してください。"
    header-message-alignment="left"
    size="large"
    aria-title-id="modal-profile-form-title">
    <template #header>
      <h3 class="h3 u-text__bold">
        プロフィール入力
      </h3>
    </template>

    <template #description>
      <FormDefault
        ref="formEl"
        :name="form.name"
        :action="form.action"
        :method="form.method">
        <template v-if="userLeadInfo.careerId && userLeadInfo.careerId.value">
          <StepWizard
            ref="stepWizardEl"
            :scroll-by-step="false"
            :screen-lock-by-step="false">
            <ValidationObserver
              ref="validationObserverProfile"
              :namespace="namespaceByStep.profile"
              :scroll-by-validation-error="false"
              @valid="() => stepWizardEl?.next()">
              <InputProfileName
                :namespace="namespaceByStep.profile"
                :user-lead-info="userLeadInfo"
                @update:last-name="$emit('update:lastName', $event)"
                @update:first-name="$emit('update:firstName', $event)"
                @update:last-name-kana="$emit('update:lastNameKana', $event)"
                @update:first-name-kana="$emit('update:firstNameKana', $event)" />

              <ButtonSubmit
                :is-link="false"
                :has-shadow="false"
                :width="200"
                prevent
                stop
                @click:submit="validate(validationObserverProfile)">
                次へ（{{ stepWizardEl?.currentStep }}/{{ stepWizardEl?.maxStep }}）
              </ButtonSubmit>
            </ValidationObserver>

            <ValidationObserver
              ref="validationObserverCareer"
              :namespace="namespaceByStep.career"
              :scroll-by-validation-error="false"
              @valid="submit">
              <InputProfileCareer
                :namespace="namespaceByStep.career"
                :user-lead-info="userLeadInfo"
                @update:company-name="$emit('update:companyName', $event)"
                @select:company-lead-info="$emit('select:companyLeadInfo', $event)"
                @reset:corporate-zipcode="$emit('reset:corporateZipcode', '')"
                @update:industry="$emit('update:industry', $event)"
                @update:scale="$emit('update:scale', $event)"
                @update:profession="$emit('update:profession', $event)"
                @update:position-level="$emit('update:positionLevel', $event)"
                @update:department-and-position="$emit('update:departmentAndPosition', $event)" />

              <input
                v-if="userLeadInfo.careerId"
                class="input"
                type="hidden"
                :name="userLeadInfo.careerId.name"
                :value="userLeadInfo.careerId.value">

              <div class="l-rows">
                <div class="l-rows--row">
                  <ButtonSubmit
                    :is-link="false"
                    :width="200"
                    :has-shadow="false"
                    prevent
                    stop
                    @click:submit="validate(validationObserverCareer)">
                    登録する
                  </ButtonSubmit>
                <!-- /.l-rows--row --></div>

                <div class="l-rows--row u-text__center">
                  <button
                    class="button u-text__small"
                    @click.prevent="() => stepWizardEl?.prev()">
                    戻る
                  </button>
                <!-- /.l-rows--row --></div>
              <!-- /.l-rows --></div>
            </ValidationObserver>
          </StepWizard>
        </template>

        <template v-else>
          <p class="p u-text__error">
            ユーザ情報の取得に失敗しました。<br>
            画面を更新して再度お試しください。
          </p>

          <div class="o-note__general">
            <ul class="o-note__general--items">
              <li class="o-note__general--item u-text__small">
                <!-- 後続の o-list が first-child になるのを回避するため、文末の <br> は削除しないこと。 -->
                画面を更新しても解決しない場合は、お手数ですが BizHint のお問い合わせフォームよりご連絡ください。<br>
                <div
                  v-if="EXTERNAL_URL.bizhint_help_url"
                  class="o-list is-triangle">
                  <ul class="o-list--items">
                    <li class="o-list--item">
                      <a
                        class="a"
                        :href="`${EXTERNAL_URL.bizhint_help_url}/requests/new`">
                        BizHintヘルプセンター
                      </a>
                    </li>
                  </ul>
                <!-- /.o-list --></div>
              </li>
            </ul>
          <!-- /.o-note__general --></div>
        </template>
      </FormDefault>
    </template>
  </ModalDefault>
</template>
